import React, { useState,useEffect} from 'react';
import { supabase } from '../../lib/supabase';
import './DashboardSchoolSubjects.css';

const DashboardSchoolSubjects = ({ subjects, dataLoading, onRefresh, readOnly = false }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);
    const [subjectFormData, setSubjectFormData] = useState({ name: '' });
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [pendingCloseAction, setPendingCloseAction] = useState(null);
    const [initialFormData, setInitialFormData] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [subjectToDelete, setSubjectToDelete] = useState(null);

    const schoolId = localStorage.getItem('school_id');

    // If the dashboard enters read-only mode, force-disable any destructive UX paths.
    useEffect(() => {
        if (!readOnly) return;
        setShowAddModal(false);
        setEditingSubject(null);
    }, [readOnly]);

    // Set initial form data when modal opens
    useEffect(() => {
        if (showAddModal) {
            setInitialFormData(JSON.parse(JSON.stringify(subjectFormData)));
        } else {
            setInitialFormData(null);
        }
    }, [showAddModal]);

    // Check if form has been modified
    const hasFormChanges = () => {
        if (!initialFormData) return false;
        return JSON.stringify(subjectFormData) !== JSON.stringify(initialFormData);
    };

    // Handle overlay click with unsaved changes check
    const handleOverlayClick = (closeAction) => {
        if (hasFormChanges()) {
            setPendingCloseAction(() => closeAction);
            setShowDiscardConfirm(true);
        } else {
            closeAction();
        }
    };

    // Confirm discard changes
    const confirmDiscard = () => {
        if (pendingCloseAction) {
            pendingCloseAction();
            setPendingCloseAction(null);
        }
        setShowDiscardConfirm(false);
        setInitialFormData(null);
    };

    // Cancel discard
    const cancelDiscard = () => {
        setShowDiscardConfirm(false);
        setPendingCloseAction(null);
    };

    const showNotificationMessage = (type, message) => {
        setNotification({ show: true, type, message });
        setTimeout(() => {
            setNotification({ show: false, type: '', message: '' });
        }, 3000);
    };

    const handleAddSubject = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        try {
            const { error } = await supabase
                .from('subjects')
                .insert([{
                    school_id: schoolId,
                    name: subjectFormData.name
                }]);

            if (error) throw error;

            showNotificationMessage('success', 'Subject added successfully');
            setInitialFormData(null);
            setShowAddModal(false);
            setSubjectFormData({ name: '' });
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    const handleUpdateSubject = async (e) => {
        e.preventDefault();
        if (!editingSubject) return;
        if (readOnly) return;

        try {
            const { error } = await supabase
                .from('subjects')
                .update({ name: subjectFormData.name })
                .eq('id', editingSubject.id);

            if (error) throw error;

            showNotificationMessage('success', 'Subject updated successfully');
            setInitialFormData(null);
            setEditingSubject(null);
            setShowAddModal(false);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
        }
    };

    const handleDeleteSubject = async () => {
        if (readOnly) return;
        if (!subjectToDelete) return;

        try {
            const { error } = await supabase
                .from('subjects')
                .delete()
                .eq('id', subjectToDelete);

            if (error) throw error;

            showNotificationMessage('success', 'Subject deleted successfully');
            setShowDeleteConfirm(false);
            setSubjectToDelete(null);
            onRefresh();
        } catch (err) {
            showNotificationMessage('error', err.message);
            setShowDeleteConfirm(false);
            setSubjectToDelete(null);
        }
    };

    const confirmDeleteSubject = (subjectId) => {
        setSubjectToDelete(subjectId);
        setShowDeleteConfirm(true);
    };

    const cancelDeleteSubject = () => {
        setShowDeleteConfirm(false);
        setSubjectToDelete(null);
    };

    if (dataLoading) {
        return <div className="subjects-loading">Loading subjects...</div>;
    }

    return (
        <div className="dashboard-school-subjects">
            {notification.show && (
                <div className={`subject-notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}

            <div className="subjects-header">
                <h2>Subjects Management</h2>
                {!readOnly && (
                    <button className="add-subject-btn" onClick={() => { setShowAddModal(true); setEditingSubject(null); setSubjectFormData({ name: '' }); }}>
                        + Add Subject
                    </button>
                )}
            </div>

            {subjects.length === 0 ? (
                <div className="no-subjects">
                    <p>No subjects found. Add your first subject to get started.</p>
                </div>
            ) : (
                <div className="subjects-grid">
                    {subjects.map((subject) => (
                        <div key={subject.id} className="subject-card">
                            <div className="subject-content">
                                <h3>{subject.name}</h3>
                            </div>
                            {!readOnly && (
                                <div className="subject-actions">
                                    <button className="edit-btn" onClick={() => { setEditingSubject(subject); setSubjectFormData({ name: subject.name }); setShowAddModal(true); }}>
                                        Edit
                                    </button>
                                    <button className="delete-btn" onClick={() => confirmDeleteSubject(subject.id)}>
                                        Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Discard Changes Confirmation Dialog */}
            {showDiscardConfirm && (
                <div className="unsaved-changes-overlay" onClick={cancelDiscard}>
                    <div className="unsaved-changes-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="unsaved-changes-header">
                            <div className="unsaved-changes-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                            </div>
                            <h2>Unsaved Changes</h2>
                        </div>
                        <div className="unsaved-changes-body">
                            <p className="unsaved-changes-message">
                                You have unsaved changes. Are you sure you want to discard them?
                            </p>
                        </div>
                        <div className="unsaved-changes-actions">
                            <button className="unsaved-changes-cancel-btn" onClick={cancelDiscard}>
                                Cancel
                            </button>
                            <button className="unsaved-changes-discard-btn" onClick={confirmDiscard}>
                                Discard Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Subject Modal */}
            {!readOnly && showAddModal && (
                <div className="modal-overlay" onClick={() => handleOverlayClick(() => setShowAddModal(false))}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingSubject ? 'Edit Subject' : 'Add New Subject'}</h3>
                        <form onSubmit={editingSubject ? handleUpdateSubject : handleAddSubject}>
                            <div className="form-group">
                                <label>Subject Name *</label>
                                <input
                                    type="text"
                                    value={subjectFormData.name}
                                    onChange={(e) => setSubjectFormData({ ...subjectFormData, name: e.target.value })}
                                    required
                                    placeholder="e.g., Mathematics, Science, English, etc."
                                />
                            </div>
                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={() => handleOverlayClick(() => setShowAddModal(false))}>Cancel</button>
                                <button type="submit" className="submit-btn">{editingSubject ? 'Update' : 'Add'} Subject</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Subject Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="dashboard-school-subjects-delete-overlay" onClick={cancelDeleteSubject}>
                    <div className="dashboard-school-subjects-delete-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dashboard-school-subjects-delete-header">
                            <div className="dashboard-school-subjects-delete-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                                </svg>
                            </div>
                            <h2>Delete Subject</h2>
                        </div>
                        <div className="dashboard-school-subjects-delete-body">
                            <p className="dashboard-school-subjects-delete-message">
                                Are you sure you want to delete this subject? This action cannot be undone.
                            </p>
                        </div>
                        <div className="dashboard-school-subjects-delete-actions">
                            <button className="dashboard-school-subjects-delete-cancel-btn" onClick={cancelDeleteSubject}>
                                Cancel
                            </button>
                            <button className="dashboard-school-subjects-delete-confirm-btn" onClick={handleDeleteSubject}>
                                Delete Subject
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardSchoolSubjects;
